// @dynamic
import { Injectable, inject } from '@angular/core';

import { Product, PromotionModel, QuoteModel } from '@bytel/bytel-sales';
import {
    CartApi,
    DeliveryInfo,
    IPromotion,
    PostOrderInputResponse,
    PostOrderResponse,
    PostReceiptResponse,
    ProductApi,
    QuoteContextRenewalModel,
    SOURCE_PROSPECT,
    SchemeApi,
    SchemeApiResult,
    UserService,
} from '@bytel/checkout';

import { AddressService } from '@services/address.service';
import { FaiService } from '@services/fai.services';
import { MainCartService } from '@services/main-cart.service';
import { MobileRecoveryService } from '@services/mobile-recovery.service';

import { MobileRecoveryModel } from '@models/mobile-recovery.model';

import { BasicObject, ObjectOfString } from '@interfaces/checkout.interface';

import { Observable, concatAll, firstValueFrom, from, last, map, mergeMap, of, tap } from 'rxjs';

import { SalesApiHelper } from '../helper/sales-api.helper';
import { CheckoutOauth2ResourcesService } from './oauth2-ressources.service';
import { PrismeLoggerService } from './prisme-logger.service';
import { ProductApiService } from './product-api.service';

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private prismeLoggerService: PrismeLoggerService = inject(PrismeLoggerService);
    private oauth2Resource: CheckoutOauth2ResourcesService = inject(CheckoutOauth2ResourcesService);
    private userService: UserService = inject(UserService);
    private addressService: AddressService = inject(AddressService);
    private cartService: MainCartService = inject(MainCartService);
    private productApiService: ProductApiService = inject(ProductApiService);
    private mobileRecovery: MobileRecoveryService = inject(MobileRecoveryService);
    private faiService: FaiService = inject(FaiService);

    public pushCart(): Observable<CartApi> {
        let serializedCart: CartApi = this.serializeCart();

        if (!this.isCartReadyToPush()) {
            return of(serializedCart);
        }

        let cartObservable: Observable<CartApi>;

        if (this.cartService.cartModel.cartId) {
            cartObservable = this.pushSchemes().pipe(
                mergeMap(() => this.pushProducts()),
                mergeMap(() => this.pushPromotions()),
                mergeMap(() => of(serializedCart)),
            );
        } else {
            cartObservable = this.oauth2Resource
                .ventes()
                .panier()
                .setLocalService()
                .useSalesApi()
                .post(serializedCart)
                .pipe(
                    mergeMap((cartApi: CartApi) => {
                        serializedCart = cartApi;
                        this.unserializeApiCart(cartApi);
                        return this.pushSchemes();
                    }),
                    mergeMap(() => this.pushProducts()),
                    mergeMap(() => this.pushPromotions()),
                    mergeMap(() => of(serializedCart)),
                );
        }

        return cartObservable;
    }

    public async pushProducts(): Promise<void> {
        for (const quote of this.cartService.cartModel.quotes) {
            await firstValueFrom(this.pushQuoteProducts(quote));
        }
    }

    public pushQuoteProducts(quote: QuoteModel): Observable<Product[]> {
        const params: BasicObject = this._getApiProductsParams(quote);
        if (quote.lastProductParamsSent !== params.hash) {
            return this.oauth2Resource
                .ventes()
                .panier(this.cartService.cartModel.cartId)
                .parcours(quote.id)
                .listeProduits()
                .setLocalService()
                .useSalesApi()
                .displayErrors()
                .post(params.data)
                .pipe(
                    mergeMap((products: ProductApi[]) => {
                        if (products) {
                            products.forEach((product) => {
                                this.unserializeApiProduct(product, this.cartService.cartModel.quotes.indexOf(quote));
                            });
                        }

                        return of(quote.products);
                    }),
                    mergeMap((products: Product[]) => {
                        const delivery: DeliveryInfo = this.cartService.getDelivery();
                        if (delivery) {
                            return this.addressService.updateShippingMethods(this.cartService.cartModel).pipe(
                                mergeMap(() =>
                                    this.addressService.updateBillingAddress(this.addressService.getBilling()),
                                ),
                                mergeMap(() => of(products)),
                            );
                        }
                        return of(products);
                    }),
                    tap(() => {
                        this.cartService.cartModel.coupon = null;
                        quote.lastProductParamsSent = params.hash;
                    }),
                );
        }

        return of(null);
    }

    public pushPromotions(): Observable<IPromotion> {
        const coupon: PromotionModel = this.cartService.cartModel.promotions.coupon;
        if (coupon && !this.cartService.cartModel.coupon) {
            const params: { code: string } = {
                code: coupon.code,
            };

            return this.oauth2Resource
                .useSalesApi()
                .ventes()
                .panier(this.cartService.cartModel.cartId)
                .promotions()
                .post(params)
                .pipe(tap((promo: IPromotion) => (this.cartService.cartModel.coupon = promo.id)));
        }

        return of(null);
    }

    public pushSchemes(): Observable<boolean> {
        const obs: Observable<SchemeApi>[] = [];
        for (let i = 0; i < this.cartService.cartModel.quotes.length; i++) {
            if (this.cartService.getQuote(i).id) {
                obs.push(of(null));
            } else {
                obs.push(this.createScheme(i));
            }
        }

        return from(obs).pipe(
            concatAll(),
            last(),
            map(() => true),
        );
    }

    public pushScheme(): Observable<SchemeApi> {
        const quote: QuoteModel = this.cartService.getQuote();

        if (quote.id) {
            return of(null);
        }

        return this.createScheme();
    }

    public createScheme(quoteIndex: number = 0): Observable<SchemeApi> {
        const params: SchemeApi = this.serializeScheme(this.cartService.cartModel.quotes[quoteIndex]);
        return this.oauth2Resource
            .ventes()
            .panier(this.cartService.cartModel.cartId)
            .parcours()
            .setLocalService()
            .useSalesApi()
            .displayErrors()
            .post(params)
            .pipe(
                tap((scheme: SchemeApi) => {
                    this.unserializeApiScheme(scheme, quoteIndex);
                }),
            );
    }

    public createOrder(params: ObjectOfString): Observable<PostOrderResponse> {
        return this.oauth2Resource
            .ventes()
            .panier(params?.cartId ?? this.cartService.cartModel.cartId)
            .commande()
            .setLocalService()
            .useSalesApi()
            .displayErrors()
            .post(params)
            .pipe(tap(() => this.cartService.resetAll()));
    }

    public pushPaymentMode(url: string, params: PostOrderInputResponse): Observable<PostReceiptResponse> {
        return this.oauth2Resource.setLocalService().useSalesApi().setUrl(url).displayErrors().post(params);
    }

    public deleteCart(): Observable<Response[]> {
        return this.oauth2Resource
            .ventes()
            .panier(this.cartService.cartModel.cartId)
            .setLocalService()
            .useSalesApi()
            .delete();
    }

    public updateProduct(product: Product, quoteIndex: number = 0): Observable<ProductApi> {
        const params: BasicObject = this.productApiService.getParams(product);
        delete params.id;
        const quote: QuoteModel = this.cartService.cartModel.getQuote(quoteIndex);
        const quoteId: number = quote?.id;
        return this.oauth2Resource
            .ventes()
            .panier(this.cartService.cartModel.cartId)
            .parcours(quoteId)
            .produits(product.id.toString())
            .setLocalService()
            .useSalesApi()
            .displayErrors()
            .put(params);
    }

    public updateSchemes(): Observable<boolean> {
        const quotes = this.cartService.cartModel.quotes;
        const obs: Observable<SchemeApi>[] = quotes.map((quote, index) => this.updateScheme(index));
        return from(obs).pipe(
            concatAll(),
            last(),
            map(() => true),
        );
    }

    public updateScheme(quoteIndex: number = 0): Observable<SchemeApi> {
        const quote: QuoteModel = this.cartService.cartModel.getQuote(quoteIndex);
        const params: SchemeApi = this.serializeScheme(quote);
        const currentQuoteId: number = quote?.id;

        return this.oauth2Resource
            .ventes()
            .panier(this.cartService.cartModel.cartId)
            .parcours(currentQuoteId)
            .setLocalService()
            .useSalesApi()
            .put(params)
            .pipe(
                mergeMap((scheme: SchemeApiResult) => {
                    this.unserializeApiScheme(scheme.parcours, quoteIndex);
                    return of(scheme.parcours);
                }),
            );
    }

    public isCartReadyToPush(): boolean {
        const quote: QuoteModel = this.cartService.getQuote();
        return this.userService.user.id && quote.products.length > 0;
    }

    public isCartPushed(): boolean {
        return !!this.cartService.cartModel.cartId;
    }

    public serializeCart(): CartApi {
        return {
            idPanier: this.cartService.cartModel.cartId || null,
            parcours: this.cartService.cartModel.quotes.length ? [] : null,
            parcourCourant: this.cartService.getQuote().id || null,
            canal: 'web',
        };
    }

    public serializeScheme(quote: QuoteModel = this.cartService.getQuote()): SchemeApi {
        const userData: BasicObject = this.userService.getStoredUserData();
        const scheme: SchemeApi = {
            contextProduit: {
                contextService: {
                    parcours: {
                        typeAction: quote.id ? '' : 'INIT',
                        typeParcours: quote.context.id,
                        idPU: +this.userService.user.id,
                    },
                    client: userData?.data,
                },
            },
            consent: userData?.consent,
        };

        if (quote.isAcquisitionFix()) {
            scheme.contextProduit.contextService.acquisitionFix = {
                idEligFai: this.faiService.eligibility?.cartId,
            };

            if (!this.faiService.eligibility.cartId) {
                this.prismeLoggerService.sendLogToPrisme('Missing Cart ID', null, {
                    typeParcours: quote.context.id,
                    faiData: this.faiService.eligibility,
                });
            }
        }

        if (quote.isRenewal()) {
            scheme.contextProduit.contextService.parcours.idContrat = (
                quote.context as QuoteContextRenewalModel
            ).contractId.toString();
        }

        if (this.cartService.isBankCart()) {
            scheme.contextProduit.contextService.parcours.sourceProspect = SOURCE_PROSPECT.EURO_INFORMATION;
        }

        return scheme;
    }

    protected unserializeApiCart(cart: CartApi): void {
        this.cartService.cartModel.cartId = cart.idPanier;
        this.cartService.cartModel.userId = this.userService.user.id;
    }

    protected unserializeApiScheme(scheme: SchemeApi, quoteIndex: number = 0): void {
        this.cartService.cartModel.quotes[quoteIndex].id = scheme.idParcours;
    }

    protected unserializeApiProduct(product: ProductApi, quoteIndex: number = 0): void {
        const quote: QuoteModel = this.cartService.getQuote(quoteIndex);
        const quoteProduct: Product = quote.getProductByGencode(product.gencode);

        if (quoteProduct) {
            quoteProduct.id = product.idProduit;
        }
    }

    private _getApiProductsParams(quote: QuoteModel): BasicObject {
        const params: { products: BasicObject[]; edp: boolean } = {
            products: [],
            edp: false,
        };

        quote.products.forEach((product) => {
            if (SalesApiHelper.IsApiProduct(product)) {
                const productParams: BasicObject = this.productApiService.getParams(product);
                params.products.push(productParams);
            }
        });

        const recovery: MobileRecoveryModel = this.mobileRecovery.getRecoveryForQuote(quote);
        if (recovery?.id && recovery.isCompleted()) {
            params.products.push(recovery.getApiProduct());
        }

        return {
            data: params,
            hash: JSON.stringify(params),
        };
    }
}
